import React, { useState, useCallback, useRef } from 'react';
import { Form, Input, Button, Typography, notification, AutoComplete, Spin, Alert } from 'antd';
import { ShopOutlined, HomeOutlined, EnvironmentOutlined, InfoCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import OpeningHoursEditor from './OpeningHoursEditor';

const { Title } = Typography;
const { TextArea } = Input;

// Debounce Funktion (kopiert aus OptimizedRegistrationForm)
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

const ShopReportForm = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    // State für Autocomplete
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const searchTimeoutRef = useRef(null); // Ref für debounce timeout
    // State für Öffnungszeiten angepasst an das Format von OpeningHoursEditor
    const [openingHours, setOpeningHours] = useState({
        Monday: { opens: '', closes: '' },
        Tuesday: { opens: '', closes: '' },
        Wednesday: { opens: '', closes: '' },
        Thursday: { opens: '', closes: '' },
        Friday: { opens: '', closes: '' },
        Saturday: { opens: '', closes: '' },
        Sunday: { opens: '', closes: '' },
    });

    // Fetch address suggestions from Nominatim API (angepasst)
    const fetchAddressSuggestions = async (query) => {
        if (!query || query.length < 3) {
            setAddressSuggestions([]);
            return;
        }
        setLoadingSuggestions(true);
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&addressdetails=1&countrycodes=de&limit=5`
            );
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            const options = data.map(item => ({
                value: item.display_name, // Full display name for the dropdown
                label: item.display_name,
                addressDetails: item.address 
            }));
            setAddressSuggestions(options);
        } catch (error) {
            console.error("Error fetching address suggestions:", error);
            setAddressSuggestions([]);
        } finally {
            setLoadingSuggestions(false);
        }
    };

    // Debounced version of the fetch function
    const debouncedFetchSuggestions = useCallback(debounce(fetchAddressSuggestions, 500), []);

    // Handler for AutoComplete search input change
    const handleAddressSearch = (value) => {
        debouncedFetchSuggestions(value);
    };

    // Handler for selecting an address suggestion (angepasst für diese Form)
    const handleAddressSelect = (value, option) => {
        const details = option.addressDetails;
        if (!details) return;

        const street = details.road || '';
        const houseNumber = details.house_number || '';
        const postcode = details.postcode || '';
        const city = details.city || details.town || details.village || '';

        const fullStreetAddress = `${street} ${houseNumber}`.trim();

        // Update Ant Design form fields
        form.setFieldsValue({
            address: fullStreetAddress, // AutoComplete Feld selbst
            postalCode: postcode,
            city: city,
        });
        // Clear suggestions after selection
        setAddressSuggestions([]); 
    };

    // Wichtig: Auch bei manueller Änderung der Felder PLZ/Stadt die Vorschläge löschen
    const handleManualInputChange = () => {
        setAddressSuggestions([]);
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        // Öffnungszeiten zum Sende-Objekt hinzufügen
        const reportData = {
            ...values,
            openingHours: openingHours
        };
        console.log('Sende gemeldete Shop-Daten:', reportData);

        try {
            // API-Endpunkt für das Melden von Shops
            let apiUrl = process.env.REACT_APP_API_URL || ''; // Hole Basis-URL
            const endpointPath = '/shops/report'; // Spezifischer Endpunkt

            // Entferne einen eventuellen Slash am Ende von apiUrl
            if (apiUrl.endsWith('/')) {
                apiUrl = apiUrl.slice(0, -1);
            }

            let fullUrl;
            // Prüfe, ob apiUrl bereits mit /api endet
            if (apiUrl.endsWith('/api')) {
                 // Wenn ja, füge nur den spezifischen Pfad hinzu (ohne /api davor)
                 fullUrl = `${apiUrl}${endpointPath}`;
            } else {
                 // Wenn nein, füge /api und den spezifischen Pfad hinzu
                 fullUrl = `${apiUrl}/api${endpointPath}`;
            }
             // Fallback für den Fall, dass apiUrl komplett fehlt (verhindert //api/...)
            if (!apiUrl) {
                 fullUrl = `/api${endpointPath}`; // Relative URL, wenn keine Basis-URL gesetzt ist
            }

            console.log('Final API URL:', fullUrl); // Log zur Überprüfung

            const response = await fetch(fullUrl, { // Verwende die konstruierte URL
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(reportData), // Sende reportData (inkl. Öffnungszeiten)
            });

            const result = await response.json(); // Lese die Antwort

            if (!response.ok || !result.success) {
                // Extrahiere Fehlermeldungen, wenn vom Backend gesendet
                let errorMessage = 'Shop konnte nicht gemeldet werden.';
                if (result.errors && Array.isArray(result.errors)) {
                    errorMessage = result.errors.map(err => err.msg).join(' ');
                } else if (result.message) {
                    errorMessage = result.message;
                }
                throw new Error(errorMessage);
            }

            // Erfolgsfall
            notification.success({
                message: 'Shop erfolgreich gemeldet!',
                description: result.message || 'Vielen Dank für deinen Beitrag.' 
            });
            form.resetFields(); // Formular zurücksetzen
            // Reset Opening Hours State too (im korrekten Format)
            setOpeningHours({
                Monday: { opens: '', closes: '' },
                Tuesday: { opens: '', closes: '' },
                Wednesday: { opens: '', closes: '' },
                Thursday: { opens: '', closes: '' },
                Friday: { opens: '', closes: '' },
                Saturday: { opens: '', closes: '' },
                Sunday: { opens: '', closes: '' },
            });
            setAddressSuggestions([]); // Auch hier Vorschläge leeren

        } catch (error) {
            console.error("Fehler beim Melden des Shops:", error);
            notification.error({
                message: 'Fehler beim Melden',
                description: error.message || 'Bitte versuche es später erneut.'
            });
        } finally {
            setLoading(false); // Ladeanzeige beenden
        }
    };

    return (
        <>
            <Title level={3}>Neuen Shop melden</Title>
            <Typography.Paragraph>
                Hilf uns, die Karte aktuell zu halten! Wenn du einen Snus-Shop kennst, der noch nicht gelistet ist, kannst du ihn hier schnell eintragen.
            </Typography.Paragraph>
            
            {/* Hinweis für Shop-Besitzer */}
            <Alert
                message="Info für Shop-Besitzer"
                description="Shop-Besitzer können gemeldete Einträge später über ihr Benutzerkonto beanspruchen und verwalten."
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: '24px', maxWidth: '600px', margin: '0 auto 24px auto' }}
            />

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                requiredMark={false}
                style={{ maxWidth: '600px', margin: '0 auto' }} // Begrenzt die Breite für bessere Lesbarkeit
            >
                <Form.Item
                    name="name"
                    label="Name des Shops"
                    rules={[{ required: true, message: 'Bitte gib den Namen des Shops ein.' }]}
                >
                    <Input prefix={<ShopOutlined />} placeholder="z.B. Kiosk am Bahnhof" />
                </Form.Item>

                <Form.Item
                    name="address"
                    label="Adresse suchen und auswählen (Straße, PLZ, Stadt)"
                    rules={[{ required: true, message: 'Bitte gib die Adresse ein oder wähle einen Vorschlag.' }]}
                >
                    <AutoComplete
                        options={addressSuggestions}
                        onSelect={handleAddressSelect}
                        onSearch={handleAddressSearch}
                        placeholder="z.B. Musterstraße 1, Berlin"
                        notFoundContent={loadingSuggestions ? <Spin size="small" /> : null}
                        allowClear // Ermöglicht das Löschen der Eingabe
                    >
                        <Input prefix={<HomeOutlined />} />
                    </AutoComplete>
                </Form.Item>

                <Form.Item
                    name="postalCode"
                    label="Postleitzahl"
                    rules={[{ required: true, message: 'Bitte gib die Postleitzahl ein.' }, { pattern: /^[0-9]{5}$/, message: 'Gültige 5-stellige PLZ eingeben.' }]}
                    style={{ display: 'inline-block', width: 'calc(40% - 8px)', marginRight: '16px' }}
                >
                    <Input prefix={<EnvironmentOutlined />} placeholder="z.B. 12345" onChange={handleManualInputChange} />
                </Form.Item>

                <Form.Item
                    name="city"
                    label="Stadt"
                    rules={[{ required: true, message: 'Bitte gib die Stadt ein.' }]}
                    style={{ display: 'inline-block', width: 'calc(60% - 8px)' }}
                >
                    <Input prefix={<EnvironmentOutlined />} placeholder="z.B. Berlin" onChange={handleManualInputChange}/>
                </Form.Item>

                <Form.Item
                    name="description"
                    label="Zusätzliche Informationen (Optional)"
                >
                    <TextArea rows={3} placeholder="z.B. Große Auswahl an Siberia, neben dem Bäcker" />
                </Form.Item>

                {/* Öffnungszeiten Editor hinzugefügt */}
                <Form.Item
                    label={
                        <span>
                            <ClockCircleOutlined style={{ marginRight: '8px' }} />
                            Öffnungszeiten (Optional)
                        </span>
                    }
                >
                    <OpeningHoursEditor
                        value={openingHours}
                        onChange={setOpeningHours}
                    />
                </Form.Item>

                <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        Shop melden
                    </Button>
                </Form.Item>
            </Form>
        </>
    );
};

export default ShopReportForm; 